import { Dispatch, SetStateAction, useMemo } from "react"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"

import { AddBillReceiverDialogCore } from "../../../../billReceivers/dialog/AddBillReceiverDialogCore"
import { BillReceiverEndpointVM, BillReceiverListItemVM } from "../../../../billReceivers/billReceiverTypes"
import { BillReceiversResponse, ConfigQuery, fetchBillReceiversMS } from "../../../createOrderApi"
import { BillReceiverTypeMS, BillReceiversItemMS } from "../../../createOrderTypes"

type AddBillReceiverMSProps = Readonly<{
    type: "new" | "update"
    searchText?: string
    userId: number
    handleChooseBillReceiver: (receiver: BillReceiversItemMS) => void
    dialogState: boolean
    setDialogState: Dispatch<SetStateAction<boolean>>
    handleCreateNewBill: () => void
}>

function selectBillReceivers(data: BillReceiversResponse): BillReceiversItemMS[] {
    return data?.billReceivers ?? []
}

function toBillReceiverListItemVM(receiver: BillReceiversItemMS): BillReceiverListItemVM {
    const endpoint: BillReceiverEndpointVM =
        receiver.endpoint.type === BillReceiverTypeMS.EAN_NUMBER
            ? { kind: "ean", value: receiver.endpoint.ean }
            : { kind: "email", value: receiver.endpoint.email }

    return {
        id: String(receiver.id),
        name: receiver.name,
        cvr: receiver.vatnumber || undefined,
        endpoint,
        address: receiver.address?.displayName,
    }
}

export function AddBillReceiverMS({
    type,
    searchText = "",
    userId,
    handleChooseBillReceiver,
    dialogState,
    setDialogState,
    handleCreateNewBill,
}: AddBillReceiverMSProps) {
    const { data = [] } = useApiQuery(fetchBillReceiversMS, {
        queryName: ConfigQuery.MEETING_SERVICE_BILL_RECEIVERS,
        dependencies: {
            userId,
        },
        select: selectBillReceivers,
    })

    const receiverById = useMemo(() => new Map(data.map((receiver) => [String(receiver.id), receiver])), [data])
    const items = useMemo(() => data.map(toBillReceiverListItemVM), [data])

    return (
        <AddBillReceiverDialogCore
            type={type}
            searchText={searchText}
            items={items}
            dialogState={dialogState}
            setDialogState={setDialogState}
            onChooseBillReceiver={(receiverVm) => {
                const receiver = receiverById.get(receiverVm.id)
                if (receiver) {
                    handleChooseBillReceiver(receiver)
                }
            }}
            onCreateNewBill={handleCreateNewBill}
        />
    )
}
